// src/components/operationCenter/widgets/PortfolioMetricsOverviewWidget.tsx
// Portfolio Metrics Overview - Two-column categorized stats display

import React, { useMemo } from "react";
import type { WidgetProps, PortfolioMetricsOverviewConfig } from "@/types/dashboard";
import { usePortfolioCurrent, usePortfolioNav } from "@/hooks/portfolio";
import { useAccessToken } from "@/hooks/useAccessToken";
import type { NavPoint } from "@/types/portfolio";

type Tone = "positive" | "negative" | "neutral";

interface MetricItem {
  label: string;
  value: string;
  tone: Tone;
}

interface MetricGroup {
  category: string;
  metrics: MetricItem[];
}

const PortfolioMetricsOverviewWidget: React.FC<WidgetProps<PortfolioMetricsOverviewConfig>> = ({ widget }) => {
  const authToken = useAccessToken();

  const { data: currentData, isLoading: currentLoading, error: currentError } = usePortfolioCurrent(authToken);
  const { data: navData, isLoading: navLoading } = usePortfolioNav(authToken);

  const isLoading = currentLoading || navLoading;
  const current = useMemo(() => currentData?.data ?? {}, [currentData?.data]);

  // Get current NAV
  const currentNav = useMemo(() => {
    const series: NavPoint[] = navData?.data?.performance_data ?? [];
    return series[series.length - 1]?.portfolio_value ?? 0;
  }, [navData]);

  const formatPercent = (v?: number | null, signed = false) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '-';
    const pct = (v * 100).toFixed(1);
    return signed && v > 0 ? `+${pct}%` : `${pct}%`;
  };

  const formatPercentAbs = (v?: number | null) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '-';
    return `${(Math.abs(v) * 100).toFixed(1)}%`;
  };

  const formatRatio = (v?: number | null, digits = 2) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '-';
    return v.toFixed(digits);
  };

  const formatCurrency = (v?: number | null) => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '-';
    if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
    return `$${v.toFixed(0)}`;
  };

  const getTone = (v?: number | null): Tone => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return 'neutral';
    return v > 0 ? 'positive' : v < 0 ? 'negative' : 'neutral';
  };

  const toneClass = (tone: Tone) => {
    if (tone === 'positive') return 'text-emerald-400';
    if (tone === 'negative') return 'text-rose-400';
    return 'text-slate-100';
  };

  // Define all metrics by category (matching original PortfolioRiskStats)
  const leftColumn = useMemo((): MetricGroup[] => [
    {
      category: "Performance",
      metrics: [
        { label: "NAV", value: formatCurrency(currentNav), tone: 'neutral' as Tone },
        { label: "Return 30d", value: formatPercent(current['ret_30d'], true), tone: getTone(current['ret_30d']) },
        { label: "Return 90d", value: formatPercent(current['ret_90d'], true), tone: getTone(current['ret_90d']) },
        { label: "Vol 30d", value: formatPercentAbs(current['vol_30d']), tone: 'neutral' as Tone },
        { label: "Vol 90d", value: formatPercentAbs(current['vol_90d']), tone: 'neutral' as Tone },
        { label: "Sharpe 30d", value: formatRatio(current['sharpe_30d']), tone: getTone(current['sharpe_30d']) },
        { label: "Sharpe 90d", value: formatRatio(current['sharpe_90d']), tone: getTone(current['sharpe_90d']) },
        { label: "Sortino 30d", value: formatRatio(current['sortino_30d']), tone: getTone(current['sortino_30d']) },
        { label: "Sortino 90d", value: formatRatio(current['sortino_90d']), tone: getTone(current['sortino_90d']) },
      ],
    },
    {
      category: "Risk",
      metrics: [
        { label: "Max DD 90d", value: formatPercentAbs(current['max_drawdown_90d']), tone: 'neutral' as Tone },
        { label: "VaR 95%", value: formatPercentAbs(current['var95_30d_norm']), tone: 'neutral' as Tone },
        { label: "ES 95%", value: formatPercentAbs(current['es95_30d_norm']), tone: 'neutral' as Tone },
        { label: "HHI", value: formatRatio(current['hhi'], 3), tone: 'neutral' as Tone },
        { label: "Eff Holdings", value: formatRatio(current['effective_num_holdings'], 1), tone: 'neutral' as Tone },
        { label: "Turnover 30d", value: formatPercentAbs(current['turnover_30d']), tone: 'neutral' as Tone },
      ],
    },
  ], [current, currentNav]);

  const rightColumn = useMemo((): MetricGroup[] => [
    {
      category: "Market",
      metrics: [
        { label: "Beta BTC 90d", value: formatRatio(current['beta_btc_90d'], 3), tone: 'neutral' as Tone },
        { label: "Beta ETH 90d", value: formatRatio(current['beta_eth_90d'], 3), tone: 'neutral' as Tone },
        { label: "Corr BTC 90d", value: formatRatio(current['corr_btc_90d'], 3), tone: 'neutral' as Tone },
        { label: "Corr ETH 90d", value: formatRatio(current['corr_eth_90d'], 3), tone: 'neutral' as Tone },
        { label: "Alpha BTC 30d", value: formatRatio(current['alpha_btc_30d'], 3), tone: getTone(current['alpha_btc_30d']) },
        { label: "Alpha BTC 90d", value: formatRatio(current['alpha_btc_90d'], 3), tone: getTone(current['alpha_btc_90d']) },
      ],
    },
    {
      category: "Concentration",
      metrics: [
        { label: "Largest Position", value: formatPercent(current['largest_position_pct']), tone: 'neutral' as Tone },
        { label: "Largest Category", value: formatPercent(current['largest_category_pct']), tone: 'neutral' as Tone },
        { label: "Top 1", value: formatPercent(current['pct_nav_top1']), tone: 'neutral' as Tone },
        { label: "Top 3", value: formatPercent(current['pct_nav_top3']), tone: 'neutral' as Tone },
        { label: "Top 5", value: formatPercent(current['pct_nav_top5']), tone: 'neutral' as Tone },
        { label: "Top 10", value: formatPercent(current['pct_nav_top10']), tone: 'neutral' as Tone },
        { label: ">$50m Vol", value: formatPercent(current['pct_nav_high_volume_50m']), tone: 'neutral' as Tone },
        { label: "Stables", value: formatPercent(current['pct_nav_stablecoins']), tone: 'neutral' as Tone },
      ],
    },
  ], [current]);

  const renderColumn = (groups: MetricGroup[]) => (
    <div className="flex-1 min-w-0">
      {groups.map((group) => (
        <div key={group.category}>
          {/* Category Header */}
          <div className="px-2 py-1 bg-slate-800/40 border-y border-slate-700/20">
            <span className="text-[9px] font-medium text-slate-400 uppercase tracking-wider">
              {group.category}
            </span>
          </div>
          {/* Metrics */}
          {group.metrics.map((metric, idx) => (
            <div
              key={metric.label}
              className={`flex items-center justify-between px-2 py-1 ${
                idx % 2 === 0 ? 'bg-slate-800/20' : ''
              }`}
            >
              <span className="text-[10px] text-slate-400 truncate">{metric.label}</span>
              <span className={`text-[10px] font-semibold tabular-nums ${toneClass(metric.tone)}`}>
                {metric.value}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center px-3 py-2 border-b border-slate-700/30">
          <div className="animate-pulse h-3 w-24 bg-slate-700/50 rounded" />
        </div>
        <div className="flex-1 p-2">
          <div className="animate-pulse space-y-1">
            {[0, 1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-5 bg-slate-700/30 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (currentError) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-red-400">
        Failed to load portfolio metrics
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 bg-slate-800/30 flex-shrink-0">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {widget.title ?? "Portfolio Metrics"}
        </span>
      </div>

      {/* Two-column layout */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex divide-x divide-slate-700/30">
          {renderColumn(leftColumn)}
          {renderColumn(rightColumn)}
        </div>
      </div>
    </div>
  );
};

export default PortfolioMetricsOverviewWidget;
